import {
    AlertBannerType,
    BadgeColor,
    BadgeShape,
    BadgeSize,
    ExAlertBanner,
    ExBadge,
    ExLoader,
    ExTab,
    ExTabItem,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { FABRIC_BASE_URL } from '../config';
import { type Connector, getConnector } from '../connectors/connectors';
import CopyField from '../shared/CopyField';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import formStyles from '../shared/Form.module.css';
import Scopes from '../shared/Scopes';
import connectionStyles from './Connection.module.css';
import ConnectionTitleBar from './ConnectionTitleBar';
import styles from './ConnectionView.module.css';
import ConnectorSidebar from './ConnectorSidebar';
import ConnectorTools from './ConnectorTools';
import { type Connection, getConnection } from './connections';

interface ConnectionViewProps {
    connectorId: string;
    connectionId: string;
    clientSecret: string;
    onClose: () => void;
}

const PlaceholderSecretField = ({ label }: { label: string }) => {
    return (
        <div className={formStyles.credentialField}>
            <span className={formStyles.credentialLabel}>{label}</span>
            <code className={formStyles.credentialCode}>****************</code>
        </div>
    );
};

const ConnectionView = ({
    connectorId,
    connectionId,
    clientSecret,
    onClose,
}: ConnectionViewProps) => {
    const [connection, setConnection] = useState<Connection | null>(null);
    const [connector, setConnector] = useState<Connector | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [toolCount, setToolCount] = useState<number | null>(null);

    useEffect(() => {
        Promise.all([getConnection(connectionId), getConnector(connectorId)])
            .then(([connectionData, connectorData]) => {
                setConnection(connectionData);
                setConnector(connectorData);
            })
            .catch(() => setLoadError('Failed to load connection.'))
            .finally(() => setLoading(false));
    }, [connectorId, connectionId]);

    const optionFields = connector
        ? [...connector.appAuthOptions, ...connector.userAuthOptions]
        : [];

    const connectorHasOptions = optionFields.length > 0;
    const connectionOptionEntries = connection ? Object.entries(connection.options) : [];
    const connectionHasOptionEntries = connectionOptionEntries.length > 0;

    return (
        <div className={connectionStyles.drawerLayout}>
            {connector && <ConnectorSidebar connector={connector} />}
            <div className={connectionStyles.formColumn}>
                {loadError ? (
                    <ErrorAlertBanner onClose={() => setLoadError(null)}>
                        {loadError}
                    </ErrorAlertBanner>
                ) : loading ? (
                    <ExLoader label="Loading Config" />
                ) : connection && connector ? (
                    <>
                        <ConnectionTitleBar
                            connection={connection}
                            connectorName={connector.name}
                            onClose={onClose}
                        />
                        <div className={styles.tabCard}>
                            <ExTab
                                className={styles.tabs}
                                onSelect={(e) =>
                                    setActiveTab(
                                        (e as unknown as CustomEvent<{ selectedIndex: number }>)
                                            .detail.selectedIndex,
                                    )
                                }
                            >
                                <ExTabItem
                                    data-pendo-tabitem="config-view-details"
                                    selected={activeTab === 0}
                                >
                                    Details
                                </ExTabItem>
                                <ExTabItem
                                    data-pendo-tabitem="config-view-tools"
                                    selected={activeTab === 1}
                                >
                                    Tools
                                    {toolCount !== null && (
                                        <ExBadge
                                            className={styles.tabBadge}
                                            color={BadgeColor.BLUE}
                                            shape={BadgeShape.ROUND}
                                            size={BadgeSize.SMALL}
                                        >
                                            {String(toolCount).padStart(2, '0')}
                                        </ExBadge>
                                    )}
                                </ExTabItem>
                            </ExTab>

                            <div className={styles.tabContent}>
                                <div
                                    className={
                                        activeTab !== 0 ? styles.hidden : styles.detailsContent
                                    }
                                >
                                    <div className={styles.section}>
                                        <span className={styles.sectionLabel}>MCP Details</span>
                                        <div className={styles.authCard}>
                                            <CopyField
                                                data-pendo-copyfield="config-view-mcpdetails-copyserverurl"
                                                label="MCP Server URL"
                                                value={`${FABRIC_BASE_URL}/connector/${connectorId}/mcp`}
                                            />
                                            {connection.clientId && (
                                                <>
                                                    <CopyField
                                                        data-pendo-copyfield="config-view-mcpdetails-copyclientid"
                                                        label="Client ID"
                                                        value={connection.clientId}
                                                    />
                                                    {clientSecret ? (
                                                        <>
                                                            <ExAlertBanner
                                                                type={AlertBannerType.WARNING}
                                                                open
                                                            >
                                                                Copy your client secret now. It will
                                                                not be displayed again.
                                                            </ExAlertBanner>
                                                            <CopyField
                                                                data-pendo-copyfield="config-view-mcpdetails-copyclientsecret"
                                                                label="Client Secret"
                                                                value={clientSecret}
                                                            />
                                                        </>
                                                    ) : (
                                                        <PlaceholderSecretField label="Client Secret" />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {connectorHasOptions && connectionHasOptionEntries && (
                                        <div className={styles.section}>
                                            <span className={styles.sectionLabel}>
                                                Authentication Management
                                            </span>
                                            <div className={styles.authCard}>
                                                {connectionOptionEntries.map(([id, value]) => {
                                                    const field = optionFields.find(
                                                        (o) => o.id === id,
                                                    );
                                                    if (!field) return null;
                                                    if (field.type === 'password') {
                                                        return (
                                                            <PlaceholderSecretField
                                                                key={id}
                                                                label={field.label}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <CopyField
                                                            key={id}
                                                            data-pendo-copyfield="config-view-authmanagement-copyoption"
                                                            data-pendo-optionid={id}
                                                            label={field.label}
                                                            value={String(value)}
                                                        />
                                                    );
                                                })}
                                                <Scopes scopes={connector.scopes} />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={
                                        activeTab !== 1 ? styles.hidden : styles.detailsContent
                                    }
                                >
                                    <ConnectorTools
                                        mode="edit"
                                        connectorId={connectorId}
                                        disabledTools={connection.disabledTools}
                                        onDisabledToolsChange={(disabledTools) =>
                                            setConnection({ ...connection, disabledTools })
                                        }
                                        onToolCountChange={setToolCount}
                                        connection={connection}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default ConnectionView;
